import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, User, MapPin, Calendar, Hash, PlayCircle, CheckCircle, XCircle, AlertTriangle, Clock, CheckCircle2, Bot, Loader2, Sparkles, BarChart3, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const AdminSubmissionDetail = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null });
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [resubmissionRequired, setResubmissionRequired] = useState(false);
  const [aiReviewLoading, setAiReviewLoading] = useState(false);
  const [payoutUpdating, setPayoutUpdating] = useState(false);

  const fetchSubmission = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmission(response.data);
    } catch (error) {
      console.error("Failed to fetch submission:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmission();
  }, [id, token]);

  // Calculate proof-of-play completeness (5 fields now)
  const getCompleteness = (sub) => {
    if (!sub) return { completed: 0, total: 6, isComplete: false };
    const hasFiles = (val) => Array.isArray(val) ? val.length > 0 : !!val;
    const fields = [
      hasFiles(sub.merchandise_url),
      hasFiles(sub.proof_of_play_url),
      hasFiles(sub.bottle_console_url),
      hasFiles(sub.branding_stories_post_url),
      hasFiles(sub.instagram_analytics_url),
      sub.track_play_count > 0
    ];
    const completed = fields.filter(Boolean).length;
    return { completed, total: 6, isComplete: completed === 6 };
  };

  // Calculate deadline status
  const getDeadlineStatus = (sub) => {
    if (!sub || !sub.submission_deadline) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(sub.submission_deadline);
    deadline.setHours(0, 0, 0, 0);
    const submissionDate = sub.created_at ? new Date(sub.created_at) : null;
    
    const daysUntilDeadline = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
    
    if (submissionDate && submissionDate <= deadline) {
      return { status: "on_time", label: "On Time", daysUntilDeadline };
    } else if (daysUntilDeadline < 0) {
      return { status: "overdue", label: `${Math.abs(daysUntilDeadline)} days overdue`, daysUntilDeadline };
    } else if (daysUntilDeadline <= 3) {
      return { status: "due_soon", label: `Due in ${daysUntilDeadline} days`, daysUntilDeadline };
    }
    return { status: "on_track", label: `${daysUntilDeadline} days left`, daysUntilDeadline };
  };

  const handleApprove = async () => {
    try {
      await axios.put(
        `${API}/admin/submissions/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Submission approved successfully!");
      fetchSubmission();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve submission");
    } finally {
      setActionDialog({ open: false, type: null });
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    
    try {
      await axios.put(
        `${API}/admin/submissions/${id}/reject`,
        { 
          rejection_reason: rejectionReason,
          resubmission_required: resubmissionRequired
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Submission rejected");
      fetchSubmission();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject submission");
    } finally {
      setRejectDialog(false);
      setRejectionReason("");
      setResubmissionRequired(false);
    }
  };

  const handleAIReview = async () => {
    setAiReviewLoading(true);
    try {
      await axios.post(
        `${API}/admin/submissions/${id}/ai-review`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("AI review completed!");
      fetchSubmission();
    } catch (error) {
      toast.error(error.response?.data?.detail || "AI review failed");
    } finally {
      setAiReviewLoading(false);
    }
  };

  const handlePayoutStatusChange = async (newStatus) => {
    setPayoutUpdating(true);
    try {
      await axios.put(
        `${API}/admin/submissions/${id}/payout-status`,
        { payout_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Payout status updated to ${newStatus}`);
      fetchSubmission();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update payout status");
    } finally {
      setPayoutUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-500/20 text-gray-600",
      submitted: "bg-[#F58220]/20 text-[#F58220]",
      pending_approval: "bg-[#F58220]/20 text-[#F58220]",
      pending_agency_approval: "bg-[#F58220]/20 text-[#F58220]",
      pending_brand_approval: "bg-purple-500/20 text-purple-600",
      approved: "bg-green-500/20 text-green-600",
      rejected: "bg-[#ED5A34]/20 text-[#ED5A34]"
    };
    const labels = {
      draft: "Draft",
      submitted: "Pending Agency",
      pending_approval: "Pending Agency",
      pending_agency_approval: "Pending Agency",
      pending_brand_approval: "Pending Brand",
      approved: "Approved",
      rejected: "Rejected"
    };
    return (
      <span className={`inline-flex items-center px-4 py-2 text-sm font-semibold uppercase tracking-wider ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const MediaPreview = ({ url, type, label }) => {
    // Normalize: handle both single string (legacy) and array (new)
    const urls = Array.isArray(url) ? url : url ? [url] : [];
    
    if (urls.length === 0) {
      return (
        <div className="border border-gray-200 p-6">
          <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">{label}</p>
          <div className="bg-gray-100 aspect-video flex items-center justify-center">
            <p className="text-gray-400">Not uploaded</p>
          </div>
        </div>
      );
    }

    return (
      <div className="border border-gray-200 p-6 hover:border-[#00B4E5] transition-colors">
        <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">{label} ({urls.length})</p>
        <div className={urls.length === 1 ? "" : "grid grid-cols-2 gap-2"}>
          {urls.map((singleUrl, idx) => {
            const isVideo = type === "video" || singleUrl.includes("/video/") || singleUrl.includes(".mp4");
            const isAudio = type === "audio" || singleUrl.includes(".mp3") || singleUrl.includes(".wav");
            return (
              <div key={idx} className={`bg-gray-100 ${urls.length === 1 ? "aspect-video" : "aspect-square"} flex items-center justify-center overflow-hidden rounded`}>
                {isVideo ? (
                  <video src={singleUrl} controls className="w-full h-full object-cover" />
                ) : isAudio ? (
                  <div className="p-4 w-full">
                    <audio src={singleUrl} controls className="w-full" />
                  </div>
                ) : (
                  <img src={singleUrl} alt={label} className="w-full h-full object-cover" />
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {urls.map((singleUrl, idx) => (
            <a key={idx} href={singleUrl} target="_blank" rel="noopener noreferrer"
               className="text-xs text-[#00B4E5] hover:underline">
              {urls.length === 1 ? "Open in new tab" : `File ${idx + 1}`}
            </a>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading submission...</p>
        </div>
      </AdminLayout>
    );
  }

  if (!submission) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">Submission not found</p>
          <Link to="/admin/submissions" className="text-[#00B4E5] hover:underline">
            Back to submissions
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-submission-detail">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/admin/submissions" 
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={20} />
            Back to submissions
          </Link>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-heading text-gray-900 mb-2">{submission.artist_name}</h1>
              <p className="text-gray-600">{submission.artist_email}</p>
            </div>
            <div className="flex items-center gap-4">
              {getStatusBadge(submission.status)}
              {(submission.status === "pending_agency_approval" || submission.status === "pending_approval" || submission.status === "submitted") && (
                <div className="flex gap-2">
                  <Button
                    className="bg-green-500 hover:bg-green-600 text-white"
                    onClick={() => setActionDialog({ open: true, type: "approve" })}
                    data-testid="approve-button"
                  >
                    <CheckCircle size={18} className="mr-2" />
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    className="border-[#ED5A34] text-[#ED5A34] hover:bg-[#ED5A34] hover:text-white"
                    onClick={() => setRejectDialog(true)}
                    data-testid="reject-button"
                  >
                    <XCircle size={18} className="mr-2" />
                    Reject
                  </Button>
                </div>
              )}
              <Button
                variant="outline"
                className="border-[#00B4E5] text-[#00B4E5] hover:bg-[#00B4E5] hover:text-white"
                onClick={handleAIReview}
                disabled={aiReviewLoading}
                data-testid="ai-review-button"
              >
                {aiReviewLoading ? (
                  <Loader2 size={18} className="mr-2 animate-spin" />
                ) : (
                  <Sparkles size={18} className="mr-2" />
                )}
                {aiReviewLoading ? "Analyzing..." : "AI Review"}
              </Button>
            </div>
          </div>
        </div>

        {/* AI Review Results */}
        {submission.ai_review && (
          <div className={`border p-6 mb-6 rounded-lg ${
            submission.ai_review.overall_status === 'passed' ? 'bg-green-50 border-green-200' :
            submission.ai_review.overall_status === 'needs_review' ? 'bg-orange-50 border-orange-200' :
            'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center gap-2 mb-4">
              <Bot size={20} className={
                submission.ai_review.overall_status === 'passed' ? 'text-green-600' :
                submission.ai_review.overall_status === 'needs_review' ? 'text-orange-600' :
                'text-red-600'
              } />
              <h3 className="font-semibold text-gray-900">AI Review Results</h3>
              <span className={`text-xs px-2 py-1 rounded ${
                submission.ai_review.overall_status === 'passed' ? 'bg-green-100 text-green-700' :
                submission.ai_review.overall_status === 'needs_review' ? 'bg-orange-100 text-orange-700' :
                'bg-red-100 text-red-700'
              }`}>
                {submission.ai_review.overall_status === 'passed' ? 'All Checks Passed' :
                 submission.ai_review.overall_status === 'needs_review' ? 'Issues Flagged' :
                 'Review Error'}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {submission.ai_review.results?.map((result, idx) => (
                <div key={idx} className={`p-3 rounded border ${
                  result.status === 'passed' ? 'bg-white border-green-200' :
                  result.status === 'flagged' ? 'bg-white border-orange-200' :
                  'bg-white border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    {result.status === 'passed' ? (
                      <CheckCircle size={16} className="text-green-600" />
                    ) : result.status === 'flagged' ? (
                      <AlertTriangle size={16} className="text-orange-600" />
                    ) : (
                      <XCircle size={16} className="text-red-600" />
                    )}
                    <span className="font-medium text-sm text-gray-900">{result.field}</span>
                  </div>
                  <p className="text-xs text-gray-600 ml-6">{result.message}</p>
                  {result.confidence && (
                    <p className="text-xs text-gray-400 ml-6 mt-1">Confidence: {Math.round(result.confidence * 100)}%</p>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Reviewed at: {new Date(submission.ai_review.reviewed_at).toLocaleString()}
            </p>
          </div>
        )}

        {/* Timeline & Completeness Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {/* Completeness */}
          {(() => {
            const completeness = getCompleteness(submission);
            return (
              <div className="bg-white border border-gray-200 p-4">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Proof Completeness</p>
                <div className="flex items-center gap-2">
                  {completeness.isComplete ? (
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-green-700">
                      <CheckCircle2 size={18} />
                      Complete ({completeness.completed}/{completeness.total})
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-orange-600">
                      <AlertTriangle size={18} />
                      Incomplete ({completeness.completed}/{completeness.total})
                    </span>
                  )}
                </div>
              </div>
            );
          })()}
          
          {/* Event Date */}
          <div className="bg-white border border-gray-200 p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Event Date</p>
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-[#00B4E5]" />
              <span className="text-sm font-semibold text-gray-900">{submission.date}</span>
            </div>
          </div>
          
          {/* Submission Deadline */}
          <div className="bg-white border border-gray-200 p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Submission Deadline</p>
            {(() => {
              const deadlineStatus = getDeadlineStatus(submission);
              if (!deadlineStatus) {
                return <span className="text-sm text-gray-400">N/A</span>;
              }
              return (
                <div className={`flex items-center gap-2 text-sm font-semibold ${
                  deadlineStatus.status === 'overdue' ? 'text-red-600' :
                  deadlineStatus.status === 'due_soon' ? 'text-orange-600' :
                  deadlineStatus.status === 'on_time' ? 'text-green-600' :
                  'text-gray-600'
                }`}>
                  {deadlineStatus.status === 'overdue' ? <AlertTriangle size={18} /> :
                   deadlineStatus.status === 'on_time' ? <CheckCircle2 size={18} /> :
                   <Clock size={18} />}
                  {submission.submission_deadline} ({deadlineStatus.label})
                </div>
              );
            })()}
          </div>
          
          {/* Submission Date */}
          <div className="bg-white border border-gray-200 p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Submitted On</p>
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-gray-500" />
              <span className="text-sm font-semibold text-gray-900">{new Date(submission.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Payout Status */}
          <div className="bg-white border border-gray-200 p-4" data-testid="payout-status-card">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Payout Status</p>
            <Select 
              value={submission.payout_status || "pending"} 
              onValueChange={handlePayoutStatusChange}
              disabled={payoutUpdating}
            >
              <SelectTrigger 
                className={`h-9 text-sm font-semibold ${
                  (submission.payout_status || "pending") === "paid" ? "border-green-300 text-green-700" :
                  submission.payout_status === "processing" ? "border-amber-300 text-amber-700" :
                  "border-gray-300 text-gray-600"
                }`}
                data-testid="payout-status-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">
                  <span className="flex items-center gap-2"><DollarSign size={14} className="text-gray-500" /> Pending</span>
                </SelectItem>
                <SelectItem value="processing">
                  <span className="flex items-center gap-2"><DollarSign size={14} className="text-amber-600" /> Processing</span>
                </SelectItem>
                <SelectItem value="paid">
                  <span className="flex items-center gap-2"><DollarSign size={14} className="text-green-600" /> Paid</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Rejection Info (if rejected) */}
        {submission.status === "rejected" && submission.rejection_reason && (
          <div className="bg-red-50 border border-red-200 p-6 mb-6 rounded-lg">
            <h3 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
              <XCircle size={18} />
              Rejection Details
            </h3>
            <p className="text-red-700 mb-3">{submission.rejection_reason}</p>
            <div className="flex flex-wrap gap-4 text-sm text-red-600">
              {submission.resubmission_required && (
                <span className="inline-flex items-center gap-1 bg-red-100 px-2 py-1 rounded">
                  <AlertTriangle size={14} />
                  Resubmission Required
                </span>
              )}
              {submission.rejected_at && (
                <span>Rejected on: {new Date(submission.rejected_at).toLocaleString()}</span>
              )}
            </div>
          </div>
        )}

        {/* Artist Details */}
        <div className="bg-white border border-gray-200 p-6 mb-6">
          <h2 className="font-heading text-xl text-gray-900 mb-6">ARTIST DETAILS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-100">
                <User size={20} className="text-gray-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Name</p>
                <p className="font-medium text-gray-900">{submission.artist_name}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-100">
                <MapPin size={20} className="text-gray-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">City</p>
                <p className="font-medium text-gray-900">{submission.city}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-100">
                <MapPin size={20} className="text-gray-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Venue</p>
                <p className="font-medium text-gray-900">{submission.venue}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-100">
                <Calendar size={20} className="text-gray-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider">Date</p>
                <p className="font-medium text-gray-900">{submission.date}</p>
              </div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-100 flex items-start gap-3">
            <div className="p-2 bg-gray-100">
              <Hash size={20} className="text-gray-600" />
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider">S-User ID</p>
              <p className="font-mono text-gray-900">{submission.s_user_id}</p>
            </div>
          </div>
        </div>

        {/* Media Uploads */}
        <div className="bg-white border border-gray-200 p-6 mb-6">
          <h2 className="font-heading text-xl text-gray-900 mb-6">MEDIA UPLOADS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <MediaPreview url={submission.merchandise_url} type="image" label="Merchandise" />
            <MediaPreview url={submission.proof_of_play_url} type="video" label="Proof of Play" />
            <div className="border border-gray-200 p-6">
              <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">Track Play Count</p>
              <div className="bg-gray-100 aspect-video flex items-center justify-center">
                <div className="text-center">
                  <PlayCircle size={40} className="mx-auto text-[#00B4E5] mb-2" />
                  <p className="text-4xl font-heading text-gray-900">{submission.track_play_count}</p>
                  <p className="text-sm text-gray-500">times played</p>
                </div>
              </div>
            </div>
            <MediaPreview url={submission.bottle_console_url} type="video" label="Bottle next to Console" />
            <MediaPreview url={submission.branding_stories_post_url} type="image" label="Branding - Stories/Post" />
            <MediaPreview url={submission.instagram_analytics_url} type="image" label="Instagram Analytics" />
            {/* Instagram Impressions Display */}
            {(submission.instagram_analytics_url || submission.instagram_analytics_impressions > 0) && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 p-6 flex flex-col items-center justify-center min-h-[120px]">
                <BarChart3 size={32} className="text-purple-600 mb-2" />
                <p className="text-3xl font-heading text-gray-900">{(submission.instagram_analytics_impressions || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-500">Total Impressions</p>
              </div>
            )}
          </div>
        </div>

        {/* Timestamps */}
        <div className="text-sm text-gray-500 flex flex-wrap gap-6">
          <span>Created: {new Date(submission.created_at).toLocaleString()}</span>
          <span>Updated: {new Date(submission.updated_at).toLocaleString()}</span>
          {submission.agency_approved_at && (
            <span className="text-purple-600">Agency Approved: {new Date(submission.agency_approved_at).toLocaleString()}</span>
          )}
          {submission.brand_approved_at && (
            <span className="text-green-600">Brand Approved: {new Date(submission.brand_approved_at).toLocaleString()}</span>
          )}
        </div>

        {/* Approval Dialog */}
        <AlertDialog open={actionDialog.open && actionDialog.type === "approve"} onOpenChange={(open) => !open && setActionDialog({ open: false, type: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Approve Submission</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to approve this submission from {submission?.artist_name}? This will send it to the Brand for final review.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleApprove}
                className="bg-green-500 hover:bg-green-600"
              >
                Approve & Send to Brand
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Rejection Dialog with Reason */}
        <Dialog open={rejectDialog} onOpenChange={(open) => {
          if (!open) {
            setRejectDialog(false);
            setRejectionReason("");
            setResubmissionRequired(false);
          }
        }}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-[#ED5A34]">Reject Submission</DialogTitle>
              <DialogDescription>
                Please provide a reason for rejecting the submission from <span className="font-semibold">{submission?.artist_name}</span>. This will be visible to the artist.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="rejection-reason">Rejection Reason *</Label>
                <Textarea
                  id="rejection-reason"
                  placeholder="Enter the reason for rejection (e.g., Incomplete proof, Poor quality, Wrong format...)"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="min-h-[100px]"
                  data-testid="rejection-reason-input"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="resubmission-toggle" className="font-medium">Resubmission Required</Label>
                  <p className="text-sm text-gray-500">Allow the artist to resubmit with corrections</p>
                </div>
                <Switch
                  id="resubmission-toggle"
                  checked={resubmissionRequired}
                  onCheckedChange={setResubmissionRequired}
                  data-testid="resubmission-toggle"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setRejectDialog(false);
                setRejectionReason("");
                setResubmissionRequired(false);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
                className="bg-[#ED5A34] hover:bg-[#d44a28] text-white"
                data-testid="confirm-reject-button"
              >
                Reject Submission
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSubmissionDetail;
